import React, { useState } from 'react';
import { EventCard } from '../types';
import './MentionsEventCard.css';

interface MentionsEventCardProps {
  event: EventCard;
  onOpenMarketModal: (event: EventCard) => void;
  onMarketClick: (event: EventCard, marketIndex: number) => void;
  parlayLegs?: Array<{
    eventTitle: string;
    marketQuestion: string;
    outcome: 'yes' | 'no';
    outcomePrice: number;
    decimalOdds: number;
    endDate?: string;
    marketIcon?: string;
    marketId: string;
    sportsTeamData?: any;
  }>;
}

const MentionsEventCard: React.FC<MentionsEventCardProps> = ({ event, onOpenMarketModal, onMarketClick, parlayLegs = [] }) => {
  const [hoveredMarketIndex, setHoveredMarketIndex] = useState<number | null>(null);

  // Format date to "28 Oct" format for left side
  const formatDateForLeft = (dateString?: string) => {
    if (!dateString) return { day: '', month: '' };
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      return { day, month };
    } catch {
      return { day: '', month: '' };
    }
  };

  // Format date to "Tue, 11:00 PM" format
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const time = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      return `${dayName}, ${time}`;
    } catch {
      return '';
    }
  };

  // Format volume to "$46k", "$192k" format
  const formatVolume = (volume: number) => {
    if (volume >= 1000000) {
      return `$${Math.round(volume / 1000000)}m`;
    }
    if (volume >= 1000) {
      return `$${Math.round(volume / 1000)}k`;
    }
    return `$${volume}`;
  };

  // Calculate estimated multiplier if this market was added to parlay (for Yes outcome)
  const calculateEstimatedMultiplier = (yesPrice: number): string => {
    // Calculate existing multiplier from current parlay legs
    const existingMultiplier = parlayLegs.reduce((acc, leg) => acc * leg.decimalOdds, 1.0);

    // Calculate new odds (decimal odds = 1 / probability) for Yes outcome
    const newOdds = yesPrice > 0 ? (1 / yesPrice) : 1.0;

    // Calculate total multiplier
    const totalMultiplier = existingMultiplier * newOdds;

    // Return formatted multiplier (e.g., "2.75")
    return totalMultiplier.toFixed(2);
  };

  // Get active markets (filter out resolved ones)
  const getActiveMarkets = () => {
    // Use all_markets if available, otherwise fall back to top_markets
    const markets = event.all_markets || event.top_markets;
    return markets.filter(market => {
      const yesPrice = market.outcome_prices[0] || 0;
      const noPrice = market.outcome_prices[1] || 0;
      // Filter out resolved/extreme markets (≤0.1% or ≥99.9%)
      return !(yesPrice <= 0.001 || yesPrice >= 0.999 ||
               noPrice <= 0.001 || noPrice >= 0.999);
    });
  };

  // Get top 2 markets as keywords (truncate to reasonable length)
  const getTopMarketKeywords = (markets: any[]) => {
    return markets.slice(0, 2).map(market => {
      // Truncate long questions to keyword
      const question = market.question || '';
      if (question.length > 20) {
        return question.substring(0, 20).trim() + '...';
      }
      return question;
    });
  };

  const activeMarkets = getActiveMarkets();
  const topMarkets = activeMarkets.slice(0, 2);
  const topKeywords = getTopMarketKeywords(activeMarkets);
  const additionalCount = activeMarkets.length - 2 > 0 ? activeMarkets.length - 2 : 0;
  const dateInfo = formatDateForLeft(event.ends_at);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on a market tag or the button
    if ((e.target as HTMLElement).closest('.mentions-market-tag') ||
        (e.target as HTMLElement).closest('.mentions-trade-btn')) {
      return;
    }
    onOpenMarketModal(event);
  };

  const handleMarketTagClick = (e: React.MouseEvent, marketIndex: number) => {
    e.stopPropagation();
    onMarketClick(event, marketIndex);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenMarketModal(event);
  };

  return (
    <div className="mentions-event-card" onClick={handleCardClick}>
      <div className="mentions-event-date">
        <div className="mentions-date-day">{dateInfo.day}</div>
        <div className="mentions-date-month">{dateInfo.month}</div>
      </div>

      <div className="mentions-event-left">
        {event.image && (
          <img
            src={event.image}
            alt={event.title}
            className="mentions-event-icon"
            onError={(e) => e.currentTarget.style.display = 'none'}
          />
        )}
      </div>

      <div className="mentions-event-middle">
        <div className="mentions-event-title">{event.title}</div>
        <div className="mentions-event-meta">
          {formatDateTime(event.ends_at)}
          {formatDateTime(event.ends_at) && formatVolume(event.volume) && ' • '}
          {formatVolume(event.volume)} Vol.
        </div>
      </div>

      {/* Only show market tags if there are 2 or more active markets */}
      {activeMarkets.length > 1 && (
        <div className="mentions-event-tags">
          {topKeywords.map((keyword, index) => {
            const market = activeMarkets[index];
            const yesPrice = market?.outcome_prices[0] || 0;
            const showTooltip = hoveredMarketIndex === index && parlayLegs.length >= 1;

            return (
              <span
                key={index}
                className="mentions-market-tag"
                onClick={(e) => handleMarketTagClick(e, index)}
                onMouseEnter={() => setHoveredMarketIndex(index)}
                onMouseLeave={() => setHoveredMarketIndex(null)}
              >
                {keyword}
                {showTooltip && (
                  <span className="parlay-estimate-tooltip">
                    Estimated parlay odds if added: {calculateEstimatedMultiplier(yesPrice)}x
                  </span>
                )}
              </span>
            );
          })}
          {additionalCount > 0 && (
            <span className="mentions-market-count">+{additionalCount}</span>
          )}
        </div>
      )}

      <div className="mentions-event-right">
        <button
          className="mentions-trade-btn"
          onClick={handleButtonClick}
        >
          Add to Parlay
        </button>
      </div>
    </div>
  );
};

export default MentionsEventCard;
